import Add from '@mui/icons-material/Add';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import React, { useState, useEffect } from 'react';

import { TradingHourListProps } from './TradingHourList.props';
import { RoleBox, StructureTradingHourAddGrid, StyledTypography } from './TradingHourList.styles';
import {
  TRADING_HOURS_DEFAULT,
  TRADING_HOURS_DEFAULT_CLIENT_CUSTOMER,
} from '../../../constants/structure';
import { AddRowButton } from '../../atoms/AddRowButton';
import { Button, ButtonVariantProps } from '../../atoms/Button';
import { TypographyVariantProps } from '../../atoms/Typography';
import { TradingHours, TradingHoursData } from '../../molecules/TradingHours';

export const TradingHourList: React.FC<TradingHourListProps> = ({
  heading,
  tradingHourValue,
  hasTradingHoursEditPermission = true,
  onTradingHoursChange,
  onTradingHoursDelete,
  onTradingHoursCreate,
  isClientCustomerStructureCreation,
  flush,
}) => {
  const [formData, setFormData] = useState<TradingHoursData[]>(tradingHourValue);

  useEffect(() => {
    tradingHourValue && setFormData(tradingHourValue);
  }, [tradingHourValue]);

  const addNewForm = () => {
    const data = isClientCustomerStructureCreation
      ? TRADING_HOURS_DEFAULT_CLIENT_CUSTOMER
      : TRADING_HOURS_DEFAULT;
    setFormData([...formData, data]);
    onTradingHoursCreate([...formData, data]);
  };

  const rows = formData?.map((data, index) => (
    <TradingHours
      key={index}
      value={data}
      onChange={(updatedData) => {
        const newData = formData.map((obj, i) => (index === i ? updatedData : obj));
        setFormData(newData);
        onTradingHoursChange(newData);
      }}
      onDelete={() => {
        onTradingHoursDelete(index);
        const updatedData = [...formData];
        if (index > -1) {
          updatedData.splice(index, 1);
        }
        setFormData([...updatedData]);
      }}
      hasTradingHoursEditPermission={hasTradingHoursEditPermission}
    />
  ));

  // Inside a v4 `Panel` the old `TableContainer component={Paper}` drew an
  // elevated card within a card, and MUI's default cell padding put each row on
  // a different rhythm from the address list beside it. Flush callers get the
  // same hairline-separated stack the rest of the Location tab uses; the create
  // and client/customer pages keep the table they were built around.
  if (flush) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {rows?.map((row, index) => (
          <Box key={index} sx={{ py: 1, borderTop: index > 0 ? 1 : 0, borderColor: 'v4.border' }}>
            {row}
          </Box>
        ))}
        {hasTradingHoursEditPermission && (
          <AddRowButton
            label="Add trading hours"
            onClick={addNewForm}
            showDivider={(rows?.length ?? 0) > 0}
          />
        )}
      </Box>
    );
  }

  return (
    <RoleBox flush={flush}>
      <Grid container>
        <Grid container justifyContent="space-between" mx={flush ? 0 : 1} flex={1}>
          <Grid>
            {heading && !flush && (
              <StyledTypography variant={TypographyVariantProps.H6}>{heading}</StyledTypography>
            )}
          </Grid>
          {hasTradingHoursEditPermission && (
            <StructureTradingHourAddGrid>
              <Button
                variant={ButtonVariantProps.Secondary}
                startIcon={<Add />}
                onClick={() => {
                  addNewForm();
                }}
              >
                Add
              </Button>
            </StructureTradingHourAddGrid>
          )}
        </Grid>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableBody>
              {rows?.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Grid pt="5px">{row}</Grid>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </RoleBox>
  );
};
